// import { getToApi } from 'venuescanner-js/services/api'
import isSSR from "venuescanner-js/services/isSSR";
import userFlags from "venuescanner-js/constants/user_flags";
// import { pageLoaded } from 'venuescanner-js/tools/elemental'
import Flags from "./flags";
// import serviceURLs from '../serviceURLs.json'
import store from "storejs";
import pageUser from "../pageUser";
import moment from "moment";
import CustomEvents, { Events } from "venuescanner-js/services/windowEvents";
import controllers from "../controllers";

export const flag = {};
Object.keys(userFlags).forEach((key) => {
  flag[key.toUpperCase().replace(/[^a-z]/gi, "")] = key;
});

const localKey = "user_flags";
const cacheAge = [1, "day"];
const emptyFlags = () => {
  return Object.keys(userFlags).reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});
};
const parseFromStringCache = (flags) => {
  return Object.keys(flags).reduce((acc, key) => {
    if (userFlags[key]) {
      if (!flags[key]) {
        acc[key] = null;
        return acc;
      }
      switch (userFlags[key].type) {
        case Date:
          acc[key] = new Date(flags[key]);
          break;
        default:
          acc[key] = flags[key];
      }
    }
    return acc;
  }, {});
};
/** @module
 * @name UserFlags
 * @desc Service class for fetching and updating user flags
 */
const UserFlags = {
  versions: {
    standard: {
      controller: controllers.userService.flags.get,
      flags: null,
      inflights: [],
    },
  },
  setStorage: function (data) {
    data.userId = pageUser.user && pageUser.user._id ? pageUser.user._id : null;
    // console.log({ store: data })
    store.set(localKey, data);
  },
  getFromStorage: function () {
    let data = store.get(localKey);
    // console.log({ fromCache: data, user: pageUser.user })
    let toApply = null;
    const userId =
      pageUser.user && pageUser.user._id ? pageUser.user._id : null;
    // console.log(userId, data.userId)
    if (data) {
      if (data.userId !== userId) {
        // different user or just logged in?
        if (!data.userId && userId) {
          toApply = data.flags;
        }
        data = null;
      }
      // const data = JSON.parse(cache)
      // console.log(data.fetched)
      if (data && data.fetched && pageUser.user) {
        // if logged in and the cache is older than 'cacheAge' then return nothing
        // console.log(moment(data.fetched).toDate(), moment().subtract(...cacheAge).toDate())
        if (moment(data.fetched).isBefore(moment().subtract(...cacheAge))) {
          // console.log('Cache is too old')
          return [null, toApply];
        } else {
          if (data.flags) {
            // logged in and flags have not expired
            return [parseFromStringCache(data.flags), toApply];
          } else {
            // console.log('Flags not recorded')
          }
        }
      }
      if (!data) {
        if (!pageUser.user) {
          // no flags and not logged in then create an empty flag object and store it
          const empty = emptyFlags();
          this.setStorage({
            fetched: new Date(),
            flags: empty,
          });
          return [empty, toApply];
        }
      } else {
        return [parseFromStringCache(data.flags), toApply];
      }
    }
    // console.log('No Cache')
    return [null, toApply];
  },

  inflights: [],
  /** @function
   * @name fetch
   * @param {boolean} [force=false] - Force the fetching of the data
   * @param {boolean} [refresh=false] - Refresh the data
   * @param {string} [version=standard] - The data version to fetch
   * @desc Fetches all Flags for the user or browser
   * @returns {Promise<Flags>}
   */
  fetch: async function (force = false, refresh = false, version = "standard") {
    try {
      if (!this.versions[version])
        throw new Error(`${version} is not an accepted flag version`);
      let applyAfterLoad = null;
      if (!force) {
        if (this.versions[version].flags && !refresh)
          return this.versions[version].flags;
        const [flags, toApply] = this.getFromStorage();
        if (flags) {
          this.versions[version].flags = new Flags(flags, (flags) => {
            this.updated(flags);
          });
          if (toApply) {
            Object.keys(toApply).forEach((key) => {
              if (!this.versions[version].flags[key] && toApply[key])
                this.versions[version].flags[key] = toApply[key];
            });
          }
          return this.versions[version].flags;
        } else {
          applyAfterLoad = toApply;
        }
      }
      if (!(pageUser.user && pageUser.user._id))
        return this.versions[version].flags;
      return new Promise((resolve) => {
        this.versions[version].inflights.push(resolve);
        if (this.versions[version].inflights.length === 1)
          this.load(version, applyAfterLoad);
      });
    } catch (e) {
      console.error(e);
    }
  },
  refresh: function () {
    this.fetch(false, true);
  },
  updated: function (flags) {
    if (flags) {
      this.setStorage({ fetched: new Date(), flags });
    }
  },
  /** @function
   * @name load
   * @param {string} version - Defaults to standard
   * @param {Object} applyAfterLoad - The flags structure to apply to the loaded Flags once fetched
   * @desc Used by the class to perform the actual api call for the User Flags and then resolves all the inflights with instances of the Flags
   * @returns {Promise<Flags>}
   */
  load: function (version = "standard", applyAfterLoad) {
    if (isSSR() || window.isTest) {
      this.versions[version].flags = new Flags(emptyFlags(), (flags) => {
        this.updated(flags);
      });
      if (!isSSR() && window.isTest) {
        while (this.versions[version].inflights.length) {
          const resolve = this.versions[version].inflights.shift();
          resolve(this.versions[version].flags);
        }
      }
    } else {
      // getToApi(this.versions[version].url, { returnData: true })
      this.versions[version].controller().then((flags) => {
        this.versions[version].flags = new Flags(flags, (flags) => {
          this.updated(flags);
        });
        // console.log({ applyAfterLoad })
        if (applyAfterLoad) {
          Object.keys(applyAfterLoad).forEach((key) => {
            if (!this.versions[version].flags[key] && applyAfterLoad[key])
              this.versions[version].flags[key] = applyAfterLoad[key];
          });
        }
        this.updated(this.versions[version].flags);
        while (this.versions[version].inflights.length) {
          const resolve = this.versions[version].inflights.shift();
          resolve(this.versions[version].flags);
        }
      });
    }
  },
  // setCacheToRandomEmpty: function () {
  //   this.setStorage({ fetched: new Date(),
  //     flags: {
  //       account_manager_bubble: new Date(),
  //       cookie_warning: new Date()
  //     } })
  // }
};
window.addEventListener("load", () => {
  if (!window.UserFlags) {
    UserFlags.fetch();
    window.UserFlags = UserFlags;
    // window.createEmptyFlagsCache = () => { UserFlags.setCacheToRandomEmpty() }
    CustomEvents.on(Events.loggedIn, ({ detail }) => {
      // console.log({ detail })
      UserFlags.refresh();
    });
  }
});
// pageLoaded(async () => {
//   if (!window.UserFlags) {
//     UserFlags.fetch()
//     window.UserFlags = UserFlags
//     // window.createEmptyFlagsCache = () => { UserFlags.setCacheToRandomEmpty() }
//     CustomEvents.on(Events.loggedIn, ({ detail }) => {
//     // console.log({ detail })
//       UserFlags.refresh()
//     })
//   }
// })
export default UserFlags;
